import { Button } from "primereact/button";
import { SelectButton } from "primereact/selectbutton";

import { Dialog, DialogHeader, Dropdown, GanttBar } from "@/ds";
import { Gantt } from "@/shared/components/gantt";
import type { GanttGroup, GanttRow } from "@/shared/components/gantt";

import { BotaoIndicadores } from "@/shared/components/botao-indicadores";
import { IndicadoresDialog } from "@/shared/components/indicadores-dialog";
import { RotuloObra } from "@/shared/components/rotulo-obra";
import { LegendSwatch } from "@/shared/components/legend-swatch";
import { BARRA_LAYOUT, COLUMNS, ROW_HEIGHT } from "./comparar-simulacao-dialog.constants";
import styles from "./comparar-simulacao-dialog.module.scss";
import type {
  BarraComparada,
  CompararSimulacaoDialogViewModel,
  LinhaGantt,
  RecursoComparado,
} from "./types-comparar-simulacao-dialog";

function classes(...partes: (string | false | undefined)[]): string {
  return partes.filter(Boolean).join(" ");
}

function BarraView({ barra }: { barra: BarraComparada }) {
  return (
    <GanttBar
      start={barra.start}
      end={barra.end}
      tone={barra.tone}
      variant="segment"
      layout={BARRA_LAYOUT}
      className={classes(
        barra.pontaInicio && styles.pontaInicio,
        barra.pontaFim && styles.pontaFim,
      )}
      classNames={{ label: styles.barraRotulo }}
      title={barra.titulo}
      label={barra.rotulo}
    />
  );
}

/** Figma 9581:41055: o recurso que sai fica riscado; o que entra, em negrito. */
function RecursoCell({ recurso }: { recurso: RecursoComparado }) {
  return (
    <span className={styles.recurso}>
      <i className={`${recurso.icone} ${styles.iconeTipo}`} aria-hidden />
      <span className={styles.recursoTextos}>
        <span className={styles.tagTipo}>{recurso.categoria}</span>
        <span className={styles.recursoNomes} data-testid="comparar-recurso">
          {recurso.riscado ? <s className={styles.recursoNomeAnterior}>{recurso.riscado}</s> : null}
          {recurso.destaque ? (
            <strong className={styles.recursoNome}>{recurso.destaque}</strong>
          ) : null}
        </span>
        {recurso.contexto ? (
          <span className={styles.recursoContexto}>{recurso.contexto}</span>
        ) : null}
      </span>
    </span>
  );
}

function linhaDoGantt(linha: LinhaGantt): GanttRow {
  return {
    key: linha.chave,
    groupKey: linha.grupo,
    testId: linha.simulada ? "comparar-linha-simulado" : "comparar-linha-atual",
    className: linha.simulada ? styles.linhaSimulado : undefined,
    minHeight: ROW_HEIGHT,
    actionLabel: linha.acaoLabel,
    cellRowSpans: linha.simulada ? undefined : { 0: 2 },
    skipCells: linha.simulada ? [0] : undefined,
    cells: [
      linha.recurso ? <RecursoCell key="r" recurso={linha.recurso} /> : null,
      <span key="v" className={classes(styles.versao, linha.simulada && styles.versaoSimulado)}>
        {linha.versao}
      </span>,
    ],
    hideActions: true,
    content: (
      <>
        {linha.trechos.map((trecho) => (
          <span
            key={trecho.chave}
            className={classes(
              styles.deslocamento,
              linha.simulada ? styles.deslocamentoSimulado : styles.deslocamentoAtual,
            )}
            style={{ left: trecho.left, width: trecho.width }}
            title={linha.deslocamento ?? undefined}
            data-testid="comparar-faixa-deslocamento"
            aria-hidden
          />
        ))}
        {linha.semIntervalo ? (
          <span className={styles.itemMeta}>{linha.semIntervalo}</span>
        ) : (
          linha.barras.map((barra) => <BarraView key={barra.chave} barra={barra} />)
        )}
      </>
    ),
  };
}

export function CompararSimulacaoDialogView({
  titulo,
  subtitulo,
  mobile,
  filtro,
  indicadores,
  legenda,
  grupos,
  linhas,
  gruposAbertos,
  trocarGruposAbertos,
  scale,
  hoje,
  aplicar,
  fechar,
}: CompararSimulacaoDialogViewModel) {
  const gruposDoGantt: GanttGroup[] = grupos.map((grupo) => ({
    key: grupo.chave,
    testId: "comparar-obra",
    toggleLabel: grupo.rotuloToggle,
    label: (
      <>
        <RotuloObra
          className={styles.grupoTitulo}
          nome={grupo.identificacao.nome}
          sigla={grupo.identificacao.sigla}
        />
        <span className={styles.grupoContagem}>{grupo.contagem}</span>
      </>
    ),
  }));

  return (
    <Dialog
      visible
      size="xl"
      className={styles.dialog}
      header={<DialogHeader title={titulo} subtitle={subtitulo} />}
      footer={
        <div className={styles.acoesComparar}>
          <Button
            type="button"
            label="Fechar"
            severity="secondary"
            outlined
            onClick={fechar}
            data-testid="comparar-fechar"
          />
          {aplicar ? (
            <Button
              type="button"
              className={styles.botaoAplicar}
              icon="pi pi-check"
              label={aplicar.rotulo}
              aria-label={aplicar.rotulo}
              onClick={aplicar.executar}
              data-testid="comparar-aplicar"
            />
          ) : null}
        </div>
      }
      onHide={fechar}
      breakpoints={{ "76rem": "95vw", "48rem": "100vw" }}
      data-testid="comparar-dialog"
    >
      <div className={styles.corpo}>
        <div className={styles.filtros}>
          <div className={styles.filtroGrupo}>
            <span className={styles.filtroRotulo}>Tipo</span>
            {mobile ? (
              <Dropdown
                className={styles.filtroTipoMobile}
                value={filtro.valor}
                options={filtro.opcoes}
                optionLabel="label"
                optionValue="value"
                onChange={(event) => filtro.escolher(event.value as typeof filtro.valor)}
                aria-label="Tipo"
                data-testid="comparar-filtro"
              />
            ) : (
              <SelectButton
                className={styles.filtroTipo}
                value={filtro.valor}
                options={filtro.opcoes}
                onChange={(event) => filtro.escolher(event.value as typeof filtro.valor | null)}
                allowEmpty={false}
                aria-label="Tipo"
                data-testid="comparar-filtro"
              />
            )}
          </div>
          <div className={styles.filtroIndicadores}>
            <BotaoIndicadores onClick={indicadores.abrir} testId="comparar-indicadores" />
          </div>
        </div>

        <ul className={styles.legenda} aria-label="Legenda" data-testid="comparar-legenda">
          <li className={styles.legendaTitulo}>Legenda:</li>
          {legenda.map((item) => (
            <li key={item.kind}>
              <LegendSwatch kind={item.kind} />
              {item.label}
            </li>
          ))}
        </ul>

        {grupos.length === 0 ? (
          <p className={styles.vazio}>Nenhuma alteração deste tipo na simulação.</p>
        ) : (
          <Gantt.Root
            className={styles.gantt}
            scale={scale}
            today={hoje}
            todayLabel="Hoje"
            columns={COLUMNS}
            groups={gruposDoGantt}
            expandedGroups={gruposAbertos}
            onExpandedGroupsChange={trocarGruposAbertos}
            rows={linhas.map(linhaDoGantt)}
            withActions
            testId="comparar-gantt"
          />
        )}
      </div>
      {indicadores.aberto ? (
        <IndicadoresDialog
          opcoes={indicadores.opcoes}
          value={indicadores.valor}
          onSave={indicadores.salvar}
          onCancel={indicadores.cancelar}
        />
      ) : null}
    </Dialog>
  );
}
